import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { PersonCertification } from 'src/app/people/models/personCertification';
import { PersonCredential } from '../models/credential/personCredential';
import { BaseService } from './base.service';
import { ApiVersions } from './api-version';
import { map } from 'rxjs/operators';
import { EntityCredentialApplication } from '../models/credential/entityCredentialApplication';
import {
  PersonCredentialScenario,
  PersonCredentialScenarioStatus,
} from '../models/credential/personCredentialScenario';
import { Note } from 'src/app/people/models/note';
import { Person } from 'src/app/people/models/person';
import { PersonScenarioRequirement } from '../models/credential/personScenarioRequirement';
import { IAmEvidenceDetails } from '../models/credential/personScenarioEvidenceDetails';
import { CredentialStatus } from '../models/credential/credentialStatus';
import {
  IPersonInvoiceModel,
  PersonInvoiceModel,
} from 'src/app/people/profile/profile-payments/person-invoice-model';
import { PersonCredentialApplicationStatusUpdate } from '../models/credential/personCredentialApplicationStatusUpdate.model';

/** this service is created from CredentialSingletonFactory */
@Injectable({
  providedIn: 'root',
})
export class CredentialSingletonService extends BaseService {
  public credentialName: string;
  public entityName: string;
  private _entityId: string;
  private _baseUsersPath: string;

  public readonly personCredentials = new BehaviorSubject<PersonCredential[]>([]);
  public readonly personCredentialApplications = new BehaviorSubject<EntityCredentialApplication[]>(
    null
  );
  public personCredentialScenario: ReplaySubject<PersonCredentialScenario> =
    new ReplaySubject<PersonCredentialScenario>();
  public readonly notes = new BehaviorSubject<Note[]>([]);
  public readonly person = new BehaviorSubject<Person>(null);
  public readonly personCredentialScenarioDeleted = new Subject<string>();

  public readonly allEvidenceTypes = new BehaviorSubject<IAmEvidenceDetails>(null);

  constructor() {
    super();
  }
  setCredentialDetails(credentialName: string, entityName: string = null) {
    this.credentialName = credentialName;
    this.entityName = entityName;
  }
  updatePersonCredentialApplicationPayment = (
    entityCredentialApplicationId: string,
    credentialPaymentId: string,
    checkSubmitted: CheckSubmitted
  ): Promise<boolean> =>
    this.put(
      `${this.applicationsUrl}/${entityCredentialApplicationId}/payments/${credentialPaymentId}`,
      JSON.stringify(checkSubmitted),
      null,
      ApiVersions.v1_0
    ).toPromise();

  updateCustomPersonCredentialApplicationStatus(
    entityCredentialApplicationId: string,
    updateStatusModel: PersonCredentialApplicationStatusUpdate
  ): Promise<EntityCredentialApplication> {
    return this.post(
      `${this.applicationsUrl}/${entityCredentialApplicationId}/update-status`,
      JSON.stringify(updateStatusModel),
      null,
      ApiVersions.v1_0
    ).toPromise();
  }

  completePersonCredentialApplication(
    entityCredentialApplicationId: string,
    updateStatusModel: PersonCredentialApplicationStatusUpdate
  ): Promise<EntityCredentialApplication> {
    return this.post(
      `${this.applicationsUrl}/${entityCredentialApplicationId}/complete`,
      JSON.stringify(updateStatusModel),
      null,
      ApiVersions.v1_0
    ).toPromise();
  }

  deleteApplication = (entityCredentialApplicationId: string): Promise<boolean> =>
    this.delete(
      `${this.applicationsUrl}/${entityCredentialApplicationId}`,
      null,
      ApiVersions.v1_0,
      false
    ).toPromise();

  addPersonCredentialApplicationScenario(
    entityCredentialApplicationId: string,
    scenarioDefinitionId
  ): Promise<EntityCredentialApplication> {
    return this.post(
      `${this.applicationsUrl}/${entityCredentialApplicationId}/scenarios/${scenarioDefinitionId}`,
      null,
      null,
      ApiVersions.v1_0
    )
      .pipe(map((app) => EntityCredentialApplication.parse(app)))
      .toPromise();
  }

  getPersonCredentialApplicationDetails(
    entityCredentialApplicationId: string
  ): Promise<EntityCredentialApplication> {
    return this.get(
      `${this.applicationsUrl}/${entityCredentialApplicationId}`,
      false,
      ApiVersions.v1_0
    )
      .pipe(map((app) => EntityCredentialApplication.parse(app)))
      .toPromise();
  }

  payInvoiceByCheck = (checkSubmitted: CheckSubmitted) => {
    return this.put(
      `${this.invoiceUrl}/check`,
      JSON.stringify(checkSubmitted),
      null,
      ApiVersions.unversioned
    )
      .pipe(
        map((json: IPersonInvoiceModel) => {
          if (checkSubmitted.personRequirementId && checkSubmitted.personScenarioId) {
            return PersonScenarioRequirement.parse(json);
          }
          return new PersonInvoiceModel(json);
        })
      )
      .toPromise<PersonInvoiceModel | PersonScenarioRequirement>();
  };

  generateCredentialInvoice = (credentialFeeDefinitionId: string) =>
    this.post(
      `${this.invoiceUrl}/credential/${credentialFeeDefinitionId}/send`,
      null,
      null,
      ApiVersions.unversioned
    ).toPromise();

  getDocument(key: string): Promise<FileDetailsModel> {
    return this.get(
      `${this.s3Url}?key=${encodeURIComponent(key)}`,
      false,
      ApiVersions.v1_0
    ).toPromise();
  }

  getDocumentForCompletedEvidence(key: string): Promise<FileDetailsModel> {
    return this.get(
      `${this.evidenceDocsUrl}?key=${encodeURIComponent(key)}`,
      false,
      ApiVersions.v1_0
    ).toPromise();
  }

  addPersonCredentialApplication(
    credentialApplicationDefinitionId: string
  ): Promise<EntityCredentialApplication> {
    return this.post(
      this.applicationsUrl,
      JSON.stringify({
        credentialApplicationDefinitionId: credentialApplicationDefinitionId,
      }),
      null,
      ApiVersions.v1_0
    )
      .toPromise()
      .then((result) => {
        if (!result?.application) {
          return null; //400
        }
        const newApplication = EntityCredentialApplication.parse(result.application);
        this.personCredentialApplications.next([
          ...this.personCredentialApplications.value,
          newApplication,
        ]);
        if (
          result.credential &&
          this.personCredentials.value.findIndex((x) => x.id == result.credential.id) == -1
        ) {
          this.personCredentials.next([
            ...this.personCredentials.value,
            PersonCredential.parse(result.credential),
          ]);
        }
        return newApplication;
      });
  }
  updatePersonCredentialScenario(
    entityCredentialScenarioId: string,
    payload: { status: PersonCredentialScenarioStatus; note?: string }
  ): Promise<boolean> {
    return this.put(
      `${this.scenarioUrl}/${entityCredentialScenarioId}`,
      JSON.stringify(payload),
      null,
      ApiVersions.v1_0
    )
      .toPromise()
      .then((json) => {
        if (!json) {
          return false;
        }
        const model = this.parseScenarioAndUpdateObservable(json);
        if (model.status !== 'Completed') {
          return true;
        }

        //scenario was completed, update the credential
        this.personCredentials.next([
          ...this.personCredentials.value.map((c) => {
            if (c.credentialDefinitionId === model.credentialDefinitionId) {
              c.status = CredentialStatus.Active;
            }
            return c;
          }),
        ]);
        return true;
      });
  }

  getNotes() {
    this.get(this.notesUrl, false, ApiVersions.v1_0).subscribe((data) => {
      if (data) {
        this.notes.next(data);
      }
    });
  }

  async deletePersonCredentialSpecialty(
    userId: string,
    personCredentialApplicationId: string,
    credentialSpecialtyDefinitionId: string
  ): Promise<boolean> {
    const path = `${this.applicationsUrl}/${personCredentialApplicationId}/specialty/${credentialSpecialtyDefinitionId}`;
    return await this.delete(path, null, ApiVersions.v1_0).toPromise();
  }

  completeRequirement(
    personScenarioId: string,
    personRequirementId: string,
    payload: MarkScenarioComplete
  ): Promise<PersonScenarioRequirement> {
    const url = this.getPersonRequirementUrl(personScenarioId, personRequirementId);

    return this.put(url, JSON.stringify(payload), null, ApiVersions.v1_0)
      .pipe(map((jsonResult) => PersonScenarioRequirement.parse(jsonResult)))
      .toPromise();
  }

  getEntityName = async () => {
    if (this.entityName) {
      return this.entityName;
    }
    const json = await this.get(this.entityUrl, false, ApiVersions.v1_0, true).toPromise();
    const person = this.convertPerson(json);
    if (!person) {
      this.person.error({ message: 'Person not found', status: 404 });
      throw new Error('Person not found');
    }
    this.person.next(person);
    return person.fullName;
  };
  private convertPerson(data): Person {
    if (!data) return null;
    return new Person(data);
  }

  getPersonScenarioEvidences = async (
    personScenarioId: string,
    personRequirementId: string
  ): Promise<IAmEvidenceDetails> =>
    await this.get(
      this.getPersonRequirementUrl(personScenarioId, personRequirementId, 'evidences'),
      false,
      ApiVersions.v1_0
    )
      .toPromise()
      .then((result) => {
        this.allEvidenceTypes.next(result);
        return result;
      });

  createNote(note: Note) {
    let newNoteStream = JSON.stringify(note);
    return this.post(this.notesUrl, newNoteStream, null, ApiVersions.v1_0).pipe(
      map((note) => {
        return new Note(note);
      })
    );
  }

  getPersonAvailableCredentialsAutomations = (): Promise<{
    toComplete: PersonScenarioRequirement[];
    notComplete: PersonScenarioRequirement[];
  }> =>
    this.get(`${this.credentialsUrl}/automations`, false, ApiVersions.v1_0)
      .pipe(map((jsonResult) => jsonResult))
      .toPromise();

  queuePersonAvailableCredentialsAutomations = () =>
    this.post(`${this.credentialsUrl}/automations`, null, null, ApiVersions.v1_0).toPromise();

  deletePersonCredentialScenario(entityCredentialScenarioId: string): Promise<boolean> {
    return this.delete(`${this.scenarioUrl}/${entityCredentialScenarioId}`, null, ApiVersions.v1_0)
      .pipe(
        map(() => {
          this.personCredentialScenario.next(null);

          this.personCredentialScenarioDeleted.next(entityCredentialScenarioId);
          return true;
        })
      )
      .toPromise();
  }

  getPersonCredentialScenario = (entityCredentialScenarioId: string) =>
    this.get(`${this.scenarioUrl}/${entityCredentialScenarioId}`, false, ApiVersions.v1_0)
      .toPromise()
      .then((json) => this.parseScenarioAndUpdateObservable(json));

  private parseScenarioAndUpdateObservable(json: any) {
    const model = PersonCredentialScenario.parseWithRequirement(json);
    this.personCredentialScenario.next(model);
    return model;
  }

  getPersonCredentials() {
    this.get(this.credentialsUrl, false, ApiVersions.v1_0)
      .pipe(map(this.convertPersonCredentialsData))
      .subscribe((data) => {
        if (data) {
          this.personCredentials.next(data);
        }
      });
  }

  changeManualStatus = (
    personCredentialId: string,
    manualStatusId: string,
    note: string
  ): Promise<boolean> =>
    this.patch(
      `${this.credentialsUrl}/${personCredentialId}/change-manual-status`,
      JSON.stringify({
        manualStatusId: manualStatusId,
        note: note,
      }),
      ApiVersions.v0_1
    ).toPromise();

  getPersonCredentialApplications = () =>
    this.get(this.applicationsUrl, false, ApiVersions.v1_0)
      .toPromise()
      .then((json) => {
        const model = this.convertPersonCredentialApplicationsData(json);
        this.personCredentialApplications.next(model);
        return model;
      });

  private convertPersonCredentialApplicationsData(data): EntityCredentialApplication[] {
    return data.map((credentialApps) => EntityCredentialApplication.parse(credentialApps));
  }

  private convertPersonCredentialsData(data): PersonCredential[] {
    return data.map((personCredential) => PersonCredential.parse(personCredential));
  }

  init(route: ActivatedRouteSnapshot) {
    if (!route?.params) {
      console.error(`CredentialSingletonService: Route params are not available.`);
      this._entityId = null;
    }
    console.log(route.params);
    if (route.params.personId) {
      this._entityId = route.params.personId;
      this._baseUsersPath = 'users';
    } else if (route.parent?.params?.personId) {
      this._entityId = route.parent.params.personId;
      this._baseUsersPath = 'users';
    } else if (route.params.teamId) {
      this._entityId = route.params.teamId;
      this._baseUsersPath = 'teams';
    } else if (route.parent?.params?.teamId) {
      this._entityId = route.parent.params.teamId;
      this._baseUsersPath = 'teams';
    } else {
      console.error(
        `CredentialSingletonService: Unable to initialize. Route params do not contain personId or teamId.`
      );
      this._baseUsersPath = null;
      this._entityId = null;
    }
  }

  get isPerson(): boolean {
    return this._baseUsersPath === 'users';
  }

  get isTeam(): boolean {
    return this._baseUsersPath === 'teams';
  }

  get getEntityId(): string {
    if (!this._entityId) {
      throw new Error(
        'Entity ID is not initialized. Please make sure CredentialSingletonServiceResolver is used in the route.'
      );
    }
    return this._entityId;
  }

  private getPersonRequirementUrl = (
    personScenarioId: string,
    personRequirementId: string,
    resource: string = null
  ) =>
    resource
      ? `${this.scenarioUrl}/${personScenarioId}/requirements/${personRequirementId}/${resource}`
      : `${this.scenarioUrl}/${personScenarioId}/requirements/${personRequirementId}`;

  get credentialsUrl(): string {
    return `${this._baseUsersPath}/${this.getEntityId}/credentials`;
  }

  get applicationsUrl(): string {
    return `${this._baseUsersPath}/${this.getEntityId}/applications`;
  }
  get scenarioUrl(): string {
    return `${this._baseUsersPath}/${this.getEntityId}/scenarios`;
  }
  get notesUrl(): string {
    return `${this._baseUsersPath}/${this.getEntityId}/notes`;
  }
  get entityUrl(): string {
    return `${this._baseUsersPath}/${this.getEntityId}`;
  }
  get evidenceDocsUrl(): string {
    return `${this._baseUsersPath}/${this.getEntityId}/evidences/signed-url`;
  }
  get s3Url(): string {
    return `${this._baseUsersPath}/${this.getEntityId}/documents/signed-url`;
  }
  get invoiceUrl(): string {
    return `${this._baseUsersPath}/${this.getEntityId}/invoices`;
  }
}

@Injectable({
  providedIn: 'root',
})
export class CredentialSingletonServiceResolver {
  constructor(private credentialService: CredentialSingletonService) {}
  resolve(route: ActivatedRouteSnapshot) {
    this.credentialService.init(route);
    return this.credentialService;
  }
}

type MarkScenarioComplete = {
  isVerified?: boolean;
  note?: string;
};

type FileDetailsModel = {
  key: string;
  url: string;
};

type CheckSubmitted = {
  nameOnCheck: string;
  checkNumber: string;
  comment: string;
  personInvoiceId: string;
  personRequirementId?: string;
  personScenarioId?: string;
};
